import React, {useMemo} from 'react';
import {Linking, Platform, ScrollView, StyleSheet, TextStyle, View} from 'react-native';
import {Text} from './Themed';
import {styles as appStyles} from '../constants/Styles';
import Colors from '../constants/Colors';
import {parseResponse, ResponseNode, responseText, safeResponseUrl} from '../utils/responseMarkup';

const hiddenTags = new Set(['script', 'style', 'head', 'iframe', 'object', 'embed', 'template', 'svg', 'math']);
const blockTags = new Set([
  'p', 'div', 'section', 'article', 'main', 'header', 'footer', 'html', 'body',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre',
  'hr', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'dl', 'dt', 'dd',
]);
const inlineStyles: Record<string, TextStyle> = {
  strong: {fontWeight: '700'}, b: {fontWeight: '700'},
  em: {fontStyle: 'italic'}, i: {fontStyle: 'italic'},
  s: {textDecorationLine: 'line-through'}, del: {textDecorationLine: 'line-through'},
  u: {textDecorationLine: 'underline'}, code: {fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace'},
};

function renderInline(nodes: ResponseNode[], prefix: string): React.ReactNode[] {
  return nodes.map((node, index) => {
    const key = `${prefix}-${index}`;
    if (node.type === 'text') {
      const previous = nodes[index - 1];
      return previous && 'attribs' in previous && previous.name === 'br'
        ? node.data.replace(/^\r?\n/, '') : node.data;
    }
    if (!('attribs' in node) || hiddenTags.has(node.name)) return null;
    if (node.name === 'br') return '\n';
    if (node.name === 'img') return node.attribs.alt || '';
    const children = renderInline(node.children, key);
    if (node.name === 'a') {
      const url = safeResponseUrl(node.attribs.href || '');
      return <Text key={key} style={url ? styles.link : undefined}
                   accessibilityRole={url ? 'link' : undefined}
                   onPress={url ? () => { Linking.openURL(url).catch(() => {}); } : undefined}>
        {children}
      </Text>;
    }
    // Returned attributes, CSS and event handlers are never applied to views.
    return <Text key={key} style={inlineStyles[node.name]}>{children}</Text>;
  });
}

function renderBlocks(nodes: ResponseNode[], prefix: string): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let inline: ResponseNode[] = [];
  const flush = () => {
    if (inline.some(node => node.type !== 'text' || node.data.trim())) {
      const key = `${prefix}-text-${result.length}`;
      result.push(<Text key={key} selectable style={styles.paragraph}>{renderInline(inline, key)}</Text>);
    }
    inline = [];
  };
  nodes.forEach((node, index) => {
    if ('attribs' in node && hiddenTags.has(node.name)) return;
    if ('attribs' in node && blockTags.has(node.name)) {
      flush();
      result.push(renderBlock(node, `${prefix}-${index}`));
    } else {
      inline.push(node);
    }
  });
  flush();
  return result;
}

function tableRows(nodes: ResponseNode[]): ResponseNode[] {
  return nodes.flatMap(node => {
    if (!('attribs' in node)) return [];
    if (node.name === 'tr') return [node];
    return ['thead', 'tbody', 'tfoot'].includes(node.name) ? tableRows(node.children) : [];
  });
}

function renderBlock(node: ResponseNode, key: string): React.ReactNode {
  if (!('attribs' in node)) return null;
  const {name, children} = node;
  if (/^h[1-6]$/.test(name)) {
    const level = Number(name[1]);
    return <Text key={key} selectable accessibilityRole="header"
                 style={[styles.heading, {fontSize: [28, 24, 21, 19, 18, 17][level - 1]}]}>
      {renderInline(children, key)}
    </Text>;
  }
  if (name === 'pre') {
    return <ScrollView key={key} horizontal style={styles.codeBlock}>
      <Text selectable style={styles.code}>{responseText(node).replace(/\n$/, '')}</Text>
    </ScrollView>;
  }
  if (name === 'hr') return <View key={key} style={styles.rule}/>;
  if (name === 'ul' || name === 'ol') {
    const start = Number.parseInt(node.attribs.start || '1', 10);
    const items = children.filter(child => 'attribs' in child && child.name === 'li');
    return <View key={key} style={styles.list}>
      {items.map((item, index) => 'children' in item &&
        <View key={`${key}-${index}`} style={styles.listItem}>
          <Text style={styles.marker}>{name === 'ul' ? '\u2022' : `${(Number.isFinite(start) ? start : 1) + index}.`}</Text>
          <View style={styles.listContent}>{renderBlocks(item.children, `${key}-${index}`)}</View>
        </View>
      )}
    </View>;
  }
  if (name === 'table') {
    return <ScrollView key={key} horizontal style={styles.table}>
      <View>
        {tableRows(children).map((row, index) => 'children' in row &&
          <View key={`${key}-${index}`} style={styles.tableRow}>
            {row.children.filter(cell => 'attribs' in cell && ['td', 'th'].includes(cell.name))
              .map((cell, cellIndex) => 'attribs' in cell &&
                <View key={`${key}-${index}-${cellIndex}`} style={styles.tableCell}>
                  <Text selectable style={[styles.text, cell.name === 'th' && inlineStyles.strong]}>
                    {renderInline(cell.children, `${key}-${index}-${cellIndex}`)}
                  </Text>
                </View>
              )}
          </View>
        )}
      </View>
    </ScrollView>;
  }
  return <View key={key} style={name === 'blockquote' ? styles.quote : undefined}>
    {renderBlocks(children, key)}
  </View>;
}

export default function FormattedResponse({content}: {content?: string | null}) {
  const nodes = useMemo(() => parseResponse(content || ''), [content]);
  if (!content) return null;
  return <View style={styles.response}>
    <View style={[appStyles.card, styles.card]}>{renderBlocks(nodes, 'response')}</View>
  </View>;
}

const styles = StyleSheet.create({
  response: {width: '100%', paddingHorizontal: 24},
  card: {alignSelf: 'center', width: '100%', marginHorizontal: 0},
  text: {fontSize: 16, lineHeight: 24, color: Colors.dark.text},
  paragraph: {fontSize: 16, lineHeight: 24, marginBottom: 12},
  heading: {fontWeight: '700', marginTop: 8, marginBottom: 12},
  link: {color: '#b9d8ff', textDecorationLine: 'underline'},
  list: {marginBottom: 8},
  listItem: {flexDirection: 'row', alignItems: 'flex-start'},
  marker: {fontSize: 16, lineHeight: 24, minWidth: 28, paddingRight: 8},
  listContent: {flex: 1, minWidth: 0},
  quote: {borderLeftWidth: 3, borderLeftColor: Colors.dark.tint, paddingLeft: 12, marginVertical: 8},
  codeBlock: {backgroundColor: '#080d13', borderRadius: 6, marginBottom: 12},
  code: {fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace', fontSize: 14, lineHeight: 21, padding: 12},
  rule: {height: 1, backgroundColor: Colors.dark.text, opacity: 0.3, marginVertical: 12},
  table: {marginBottom: 12},
  tableRow: {flexDirection: 'row'},
  tableCell: {width: 160, padding: 8, borderWidth: 1, borderColor: '#536273'},
});
